import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MenuItem, SIDEBAR_MENU } from '../layoutConstant';
import { Footer } from '../footer/footer';
import { CommonModule } from '@angular/common';
import { CommonService } from '../../shared/services/common-service';
import { LucideAngularModule, ChevronUp } from 'lucide-angular';
import { LoginService } from '../../shared/services/login/login.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  imports: [RouterModule, Footer, CommonModule, LucideAngularModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  standalone: true,
})
export class Sidebar implements OnInit, OnDestroy {
  menuItems: MenuItem[] = SIDEBAR_MENU;
  expandedMenuId: string | null = null;
  readonly ChevronUp = ChevronUp;
  router: Router;
  private routerSubscription?: Subscription;

  // Separate top and bottom menu items
  get topMenuItems(): MenuItem[] {
    // Everything before the separator
    const separatorIndex = this.menuItems.findIndex((item) => item.isSeparator);
    return separatorIndex >= 0 ? this.menuItems.slice(0, separatorIndex) : this.menuItems;
  }

  get bottomMenuItems(): MenuItem[] {
    // Separator and everything after it
    const separatorIndex = this.menuItems.findIndex((item) => item.isSeparator);
    return separatorIndex >= 0 ? this.menuItems.slice(separatorIndex) : [];
  }

  constructor(
    public _commonService: CommonService,
    router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private authService: AuthService,
  ) {
    this.router = router;
  }

  ngOnInit(): void {
    // Initialize expanded state based on current route (only when sidebar is open)
    if (!this._commonService.sidebarHidden()) {
      this.updateExpandedState();
    }

    // Listen to route changes
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this._commonService.sidebarHidden()) {
          this.updateExpandedState();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private updateExpandedState(): void {
    // Performance no longer has expandable sub-menu items.
  }

  toggleSubMenu(menuId: string): void {
    if (this.expandedMenuId === menuId) {
      this.expandedMenuId = null;
    } else {
      this.expandedMenuId = menuId;
    }
  }

  isExpanded(menuId: string): boolean {
    return this.expandedMenuId === menuId;
  }

  isActive(menuId: string): boolean {
    if (menuId === 'performance-stats') {
      return (
        this.router.url === '/performance-stats' ||
        this.router.url.startsWith('/performance-stats') ||
        this.router.url.startsWith('/final-summary-performance') ||
        this.router.url.startsWith('/cumulative-performances')
      );
    }
    if (menuId === 'broker-settings') {
      return (
        this.router.url === '/broker-settings' ||
        this.router.url.startsWith('/broker-settings/') ||
        this.router.url === '/add-broker' ||
        this.router.url.startsWith('/add-broker/')
      );
    }
    return this.router.url === `/${menuId}` || this.router.url.startsWith(`/${menuId}/`);
  }

  isSubMenuActive(subMenuId: string): boolean {
    if (this.router.url !== '/performance-stats') {
      return false;
    }

    const queryParams = this.route.snapshot.queryParams;
    if (subMenuId === 'profit-loss') {
      return !queryParams['tab'] || queryParams['tab'] === 'profit-loss';
    } else if (subMenuId === 'historical-perf') {
      return queryParams['tab'] === 'historical-perf';
    } else if (subMenuId === 'cumulative') {
      return queryParams['tab'] === 'cumulative-performances';
    } else if (subMenuId === 'final-summary') {
      return queryParams['tab'] === 'final-summary-performance';
    }

    return false;
  }

  isSubTabActive(subTabId: string): boolean {
    // Check if we're on the performance-stats route
    if (!this.router.url.startsWith('/performance-stats')) {
      return false;
    }

    const queryParams = this.route.snapshot.queryParams;
    const currentTab = queryParams['tab'];

    // If no tab param, default to profit-loss
    if (!currentTab) {
      return subTabId === 'profit-loss';
    }

    // Direct match: sub-menu IDs match query param values
    return currentTab === subTabId;
  }

  handleMenuClick(menuItem: MenuItem, subMenuItem?: { name: string; id: string }): void {
    if (!menuItem.id) return;

    if (menuItem.id === 'logout') {
      // Use AuthService to properly clear authentication
      this.authService.logout();
      // Update login state signal
      this.loginService.loginStateChange(false);
      // Navigate to login page
      this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }

    if (subMenuItem) {
      // Navigate to performance-stats with the appropriate tab
      if (subMenuItem.id === 'profit-loss') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'profit-loss' } });
      } else if (subMenuItem.id === 'historical-perf') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'historical-perf' } });
      } else if (subMenuItem.id === 'cumulative') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'cumulative' } });
      } else if (subMenuItem.id === 'final-summary') {
        this.router.navigate(['/performance-stats'], {
          queryParams: { tab: 'final-summary' },
        });
      }
    } else if (menuItem.id === 'performance-stats') {
      this.router.navigate(['/performance-stats'], { queryParams: { tab: 'cumulative' } });
    } else {
      // Regular navigation
      this.router.navigate([`/${menuItem.id}`]);
    }
  }
}
